import { useId, useMemo, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Button, CircularProgress, DialogContent } from '@mui/material';
import { getApiErrorMessage } from '@api/getApiError';
import useCurrentTerminal from '@/Hooks/useCurrentTerminal';
import {
  useFetchConsignmentQuery,
  usePatchConsignmentMutation,
  useSendRailBillingMutation
} from '@api/Trinium/Endpoints/Consignment/slice';
import FormikModalHeader from '@components/Modals/FormikModalHeader';
import FormikModal from '@components/Modals/FormikModal';
import { useModalProvider } from '@components/Modals/ModalContext';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import Form from './Form';
import { RailBillingForm, RailBillingProps, RailBillingValidationSchema, initialFormValues } from './types';

import applicationModalStyles from '@components/Modals/ApplicationModal.module.css';
import styles from './RailBilling.module.css';

export default function RailBilling({ consignmentId, dispatchLeg }: RailBillingProps) {
  const { number: terminalNumber } = useCurrentTerminal();
  const { queueModalAnnouncement } = useModalProvider();
  const formId = useId();
  const [isOpen, setIsOpen] = useState(true);

  const [sendRailBilling] = useSendRailBillingMutation();
  const [patchConsignment, { isLoading: pendingSaveRequest }] = usePatchConsignmentMutation();

  const { data: consignment, isFetching: fetchingConsignment } = useFetchConsignmentQuery(
    {
      terminalNumber,
      consignmentId: consignmentId
    },
    {
      skip: !consignmentId
    }
  );

  /** Initial Formik Values */
  const initialValues = useMemo(() => {
    // Wait for fetchingConsignment resolve
    if (!consignment || fetchingConsignment) {
      return undefined;
    }

    const bookingNo = dispatchLeg.jobCategoryTransportationType === 'Export' ? dispatchLeg.bookingRefNo : dispatchLeg.masterBol;

    /** Set Email Subject/Body defaults based on the Dispatch Leg / Consignment values */
    const email = {
      emailSubject: `Rail Billing for Bkg ${bookingNo ?? '[unknown]'} for Container ${dispatchLeg.cntrId ?? '[unknown]'}`.toUpperCase(),
      emailBody: `This is to inform you that the following shipment has been picked up.
				\rCustomer: ${dispatchLeg.custName ?? ''}\rBooking No.: ${bookingNo ?? ''}\rCustomer Ref: ${
          dispatchLeg.csnCustRefNo ?? ''
        }\rOrder #: ${String(dispatchLeg.jobId)}\rContainer No.: ${
          dispatchLeg.cntrId ?? ''
        }\rChassis No.: ${dispatchLeg.chassisId ?? ''}\rHazardous: ${dispatchLeg.isHazmat ? 'YES' : 'NO'}\rHazardous Class of Material: ${
          consignment.haz?.unNos
            ?.map((hz) => {
              return `\r\tClass: ${hz.classId ?? '-'}\r\tU/N Number: ${hz.id}\r\tName: ${hz.description}`;
            })
            .join('\r') ?? '\r'
        }\rPickup From: ${dispatchLeg.puName ?? ''}\rDeliver To: ${
          dispatchLeg.delvName ?? ''
        }\rPieces: ${String(dispatchLeg.pcs ?? '')}\rWeight: ${String(
          dispatchLeg.totWgt ?? ''
        )}\rSeal: ${dispatchLeg.puSealNo ?? ''}`.toUpperCase()
    };

    return {
      ...initialFormValues,
      ...email
    };
  }, [dispatchLeg, consignment, fetchingConsignment]);

  async function updateRailBilling() {
    try {
      await patchConsignment({
        terminalNumber,
        consignment: {
          id: consignmentId,
          isRailBilled: true
        }
      }).unwrap();
      setIsOpen(false);
    } catch (error: unknown) {
      const errorMessage = getApiErrorMessage(error);
      queueModalAnnouncement({
        severity: 'error',
        message: errorMessage
      });
    }
  }

  async function onSubmitForm(values: RailBillingForm, formikHelpers: FormikHelpers<RailBillingForm>) {
    const validationErrors = await formikHelpers.validateForm(values);

    if (isFormikErrorsEmpty(validationErrors) && initialValues) {
      try {
        await sendRailBilling({
          terminalNumber,
          consignmentId,
          emailAddr: values.emailAddr,
          emailBody: values.emailBody,
          emailSubject: values.emailSubject
        }).unwrap();
        await updateRailBilling();
      } catch (error: unknown) {
        const errorMessage = getApiErrorMessage(error);
        queueModalAnnouncement({
          severity: 'error',
          message: errorMessage
        });
      }
    }
  }

  return (
    <FormikModal
      isOpen={isOpen}
      onClose={() => {
        setIsOpen(false);
      }}
      paperClass={styles['rail-billing']}>
      {initialValues && (
        <Formik
          initialValues={initialValues}
          validationSchema={RailBillingValidationSchema}
          onSubmit={onSubmitForm}>
          {(formikProps) => (
            <>
              <FormikModalHeader
                title='Rail Billing'
                formName={formId}
                onClose={() => {
                  setIsOpen(false);
                }}
                submitButtonText='Send'
                submitButtonMessage='Send & Close'
                areYouSurePrompt='Rail Billing was not sent, are you sure you wish to leave the form?'
                actions={
                  <>
                    <Button
                      onClick={() => {
                        void updateRailBilling();
                      }}
                      variant='contained'
                      className={styles['update-action']}
                      disabled={dispatchLeg.isRailBilled || pendingSaveRequest}>
                      {!pendingSaveRequest ? (
                        "Update but Don't Send"
                      ) : (
                        <div className={applicationModalStyles['submit-btn-spinner']}>
                          <CircularProgress size={20} />
                        </div>
                      )}
                    </Button>
                  </>
                }
              />

              <DialogContent>
                <Form
                  id={formId}
                  {...formikProps}
                />
              </DialogContent>
            </>
          )}
        </Formik>
      )}
    </FormikModal>
  );
}
